import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Swal from "sweetalert2";

import API_BASE_URL from "../config/api";

function EditSlider() {
    const { id } = useParams();
    const navigate = useNavigate();

    // Form state
    const [image, setImage] = useState(null);
    const [oldImage, setOldImage] = useState("");
    const [badge, setBadge] = useState("");
    const [title, setTitle] = useState("");
    const [description, setDescription] = useState("");
    const [order, setOrder] = useState("0");

    // Loading state
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [imageError, setImageError] = useState(false);

    /*
    |--------------------------------------------------------------------------
    | API Base URL
    |--------------------------------------------------------------------------
    */

    const baseUrl = API_BASE_URL.replace(/\/+$/, "").replace(/\/api$/, "");

    /*
    |--------------------------------------------------------------------------
    | Image URL
    |--------------------------------------------------------------------------
    | Database can contain:
    | filename.png
    | sliders/filename.png
    | storage/sliders/filename.png
    |--------------------------------------------------------------------------
    */

    const getImageUrl = (imageName) => {
        if (!imageName) return "";

        const cleanImage = String(imageName)
            .trim()
            .replace(/\\/g, "/");

        // If API already returns a full URL, use it.
        if (/^https?:\/\//i.test(cleanImage)) {
            return cleanImage;
        }

        // Extract filename from old and new database formats.
        const filename = cleanImage.split("/").filter(Boolean).pop();

        if (!filename) return "";

        return `${baseUrl}/storage/sliders/${encodeURIComponent(filename)}`;
    };

    /*
    |--------------------------------------------------------------------------
    | Fetch Slider
    |--------------------------------------------------------------------------
    */

    useEffect(() => {
        const controller = new AbortController();

        const fetchSlider = async () => {
            try {
                setLoading(true);

                const response = await fetch(
                    `${API_BASE_URL}/admin/sliders/${id}`,
                    {
                        method: "GET",
                        headers: {
                            Accept: "application/json",
                        },
                        credentials: "include",
                        signal: controller.signal,
                    }
                );

                const data = await response.json();

                if (!response.ok || !data.success || !data.slider) {
                    throw new Error(data.message || "Slider not found.");
                }

                const slider = data.slider;

                setOldImage(slider.image || "");
                setBadge(slider.badge || "");
                setTitle(slider.title || "");
                setDescription(slider.description || "");
                setOrder(String(slider.order ?? 0));
                setImage(null);
                setImageError(false);
            } catch (error) {
                if (error.name === "AbortError") return;

                console.error("Fetch Slider Error:", error);

                await Swal.fire({
                    icon: "error",
                    title: "Unable to Load Slider",
                    text: error.message || "Unable to connect with server.",
                });

                navigate("/sliders");
            } finally {
                if (!controller.signal.aborted) {
                    setLoading(false);
                }
            }
        };

        fetchSlider();

        return () => controller.abort();
    }, [id, navigate]);

    /*
    |--------------------------------------------------------------------------
    | Handle Image Change
    |--------------------------------------------------------------------------
    */

    const handleImageChange = (event) => {
        const file = event.target.files?.[0];

        if (!file) {
            setImage(null);
            return;
        }

        const allowedTypes = [
            "image/jpeg",
            "image/png",
            "image/webp",
        ];

        if (!allowedTypes.includes(file.type)) {
            Swal.fire({
                icon: "warning",
                title: "Invalid Image",
                text: "Please select a JPG, JPEG, PNG or WEBP image.",
            });

            event.target.value = "";
            setImage(null);
            return;
        }

        if (file.size > 5 * 1024 * 1024) {
            Swal.fire({
                icon: "warning",
                title: "Image Too Large",
                text: "Image size must not exceed 5 MB.",
            });

            event.target.value = "";
            setImage(null);
            return;
        }

        setImage(file);
    };

    /*
    |--------------------------------------------------------------------------
    | Update Slider
    |--------------------------------------------------------------------------
    */

    const handleSubmit = async (event) => {
        event.preventDefault();

        if (!badge.trim()) {
            Swal.fire("Badge Required", "Please enter slider badge.", "warning");
            return;
        }

        if (!title.trim()) {
            Swal.fire("Title Required", "Please enter slider title.", "warning");
            return;
        }

        if (!description.trim()) {
            Swal.fire(
                "Description Required",
                "Please enter slider description.",
                "warning"
            );
            return;
        }

        if (order === "" || !Number.isInteger(Number(order)) || Number(order) < 0) {
            Swal.fire(
                "Invalid Order",
                "Order must be a non-negative integer.",
                "warning"
            );
            return;
        }

        try {
            setSaving(true);

            const formData = new FormData();

            // Only append image if a new image was selected.
            if (image) {
                formData.append("image", image);
            }

            formData.append("badge", badge.trim());
            formData.append("title", title.trim());
            formData.append("description", description.trim());
            formData.append("order", String(order));

            // Laravel method spoofing for multipart form requests.
            formData.append("_method", "PUT");

            const response = await fetch(
                `${API_BASE_URL}/admin/sliders/${id}`,
                {
                    method: "POST",
                    headers: {
                        Accept: "application/json",
                    },
                    credentials: "include",
                    body: formData,
                }
            );

            const data = await response.json();

            if (!response.ok || !data.success) {
                let errorMessage = data.message || "Unable to update slider.";

                if (data.errors) {
                    const errors = Object.values(data.errors).flat();
                    if (errors.length) {
                        errorMessage = errors.join("\n");
                    }
                }

                throw new Error(errorMessage);
            }

            await Swal.fire({
                icon: "success",
                title: "Updated!",
                text: data.message || "Slider updated successfully.",
                timer: 1500,
                showConfirmButton: false,
            });

            navigate("/sliders");
        } catch (error) {
            console.error("Update Slider Error:", error);

            Swal.fire({
                icon: "error",
                title: "Unable to Update Slider",
                text: error.message || "Unable to connect with server.",
            });
        } finally {
            setSaving(false);
        }
    };

    /*
    |--------------------------------------------------------------------------
    | Loading UI
    |--------------------------------------------------------------------------
    */

    if (loading) {
        return (
            <div className="container-fluid py-5">
                <div className="text-center">
                    <div
                        className="spinner-border text-primary"
                        role="status"
                        aria-label="Loading"
                    />
                    <p className="mt-2">Loading slider...</p>
                </div>
            </div>
        );
    }

    /*
    |--------------------------------------------------------------------------
    | Main UI
    |--------------------------------------------------------------------------
    */

    return (
        <div
            className="container-fluid py-4"
            style={{
                maxHeight: "calc(100vh - 80px)",
                overflowY: "auto",
            }}
        >
            {/* Header */}
            <div className="d-flex justify-content-between align-items-center mb-4">
                <h3 className="mb-0">Edit Slider</h3>

                <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => navigate("/sliders")}
                    disabled={saving}
                >
                    Back
                </button>
            </div>

            {/* Form */}
            <div className="card shadow-sm">
                <div className="card-body">
                    <form onSubmit={handleSubmit}>
                        {/* Current Image */}
                        <div className="mb-4">
                            <label className="form-label fw-semibold">
                                Current Image
                            </label>

                            {oldImage && !imageError ? (
                                <div className="mt-2">
                                    <img
                                        src={getImageUrl(oldImage)}
                                        alt="Current Slider"
                                        onError={() => setImageError(true)}
                                        style={{
                                            width: "300px",
                                            maxWidth: "100%",
                                            height: "150px",
                                            objectFit: "cover",
                                            borderRadius: "5px",
                                            border: "1px solid #ddd",
                                        }}
                                    />

                                    <div className="small text-muted mt-2">
                                        {oldImage}
                                    </div>
                                </div>
                            ) : (
                                <p className="text-danger mt-2">
                                    {imageError
                                        ? "Image could not be loaded. Please check the image URL and file location."
                                        : "No current image available."}
                                </p>
                            )}
                        </div>

                        {/* Change Image */}
                        <div className="mb-3">
                            <label className="form-label fw-semibold">
                                Change Image
                            </label>

                            <input
                                type="file"
                                className="form-control"
                                accept=".jpg,.jpeg,.png,.webp,image/jpeg,image/png,image/webp"
                                onChange={handleImageChange}
                                disabled={saving}
                            />

                            <small className="text-muted">
                                Optional. Leave empty to keep the current image.
                                Maximum size: 5 MB.
                            </small>

                            {/* New Image Preview */}
                            {image && (
                                <div className="mt-3">
                                    <p className="small fw-semibold mb-2">
                                        New Image Preview
                                    </p>

                                    <img
                                        src={URL.createObjectURL(image)}
                                        alt="New Slider Preview"
                                        style={{
                                            width: "300px",
                                            maxWidth: "100%",
                                            height: "150px",
                                            objectFit: "cover",
                                            borderRadius: "5px",
                                            border: "1px solid #ddd",
                                        }}
                                    />

                                    <div className="small text-muted mt-2">
                                        {image.name}
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Badge */}
                        <div className="mb-3">
                            <label className="form-label fw-semibold">
                                Badge
                            </label>

                            <input
                                type="text"
                                className="form-control"
                                placeholder="e.g. NEET PG 2026"
                                value={badge}
                                maxLength={100}
                                onChange={(event) => setBadge(event.target.value)}
                                required
                                disabled={saving}
                            />
                        </div>

                        {/* Title */}
                        <div className="mb-3">
                            <label className="form-label fw-semibold">
                                Title
                            </label>

                            <input
                                type="text"
                                className="form-control"
                                placeholder="Enter slider title"
                                value={title}
                                maxLength={255}
                                onChange={(event) => setTitle(event.target.value)}
                                required
                                disabled={saving}
                            />
                        </div>

                        {/* Description */}
                        <div className="mb-3">
                            <label className="form-label fw-semibold">
                                Description
                            </label>

                            <textarea
                                className="form-control"
                                rows={5}
                                placeholder="Enter slider description"
                                value={description}
                                onChange={(event) =>
                                    setDescription(event.target.value)
                                }
                                required
                                disabled={saving}
                            />
                        </div>

                        {/* Order */}
                        <div className="mb-4">
                            <label className="form-label fw-semibold">
                                Order
                            </label>

                            <input
                                type="number"
                                className="form-control"
                                min="0"
                                step="1"
                                value={order}
                                onChange={(event) => setOrder(event.target.value)}
                                required
                                disabled={saving}
                            />
                        </div>

                        {/* Buttons */}
                        <div className="d-flex gap-2">
                            <button
                                type="submit"
                                className="btn btn-primary"
                                disabled={saving}
                            >
                                {saving ? (
                                    <>
                                        <span
                                            className="spinner-border spinner-border-sm me-2"
                                            role="status"
                                            aria-hidden="true"
                                        />
                                        Updating...
                                    </>
                                ) : (
                                    "Update Slider"
                                )}
                            </button>

                            <button
                                type="button"
                                className="btn btn-secondary"
                                disabled={saving}
                                onClick={() => navigate("/sliders")}
                            >
                                Cancel
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
}

export default EditSlider;
